import { DAY_END_HOUR, DAY_START_HOUR, dayjs } from "@outschool/time";
import type { SearchFilters } from "@outschool/gql-backend-generated";
import ldIsNumber from "lodash/isNumber";
import ldOmitBy from "lodash/omitBy";
import ldPick from "lodash/pick";

import * as ActivitySubjects from "../ActivitySubjects";
import * as SEO from "../SEO";
import {
  START_END_DATE_FORMAT,
  dateFilterToDayjs,
  isDateAfterOtherDates,
} from "./DateOrTime";

// Utility for manipulating a "filters" object, which represents ALL query parameters on the
// listings page.
export type SearchFilterQueryValue = string | null | undefined;
export type SearchFilterQuery = Record<string, SearchFilterQueryValue>;

type SearchFiltersRouteParams = {
  themeOrQuery?: SearchFilterQueryValue;
};

type SearchFilterParserValue =
  | SearchFilterQueryValue
  | Array<string>
  | number
  | boolean;
type SearchFilterParserFilters = Record<string, SearchFilterParserValue>;

export function slugifyTitle(title?: string | null) {
  if (title) {
    return title
      .toLowerCase()
      .replace(/\([^)]*\)/g, "")
      .replace(/[!'",:.&]/g, "")
      .replace(/[\s]/g, "-")
      .replace(/[-]+/g, "-")
      .replace(/-$/g, "");
  } else {
    return null;
  }
}

// this hash stores a list of query terms that should be mapped to actual themes
// doubtless more sophisticated ways to handle this but it's a simple first pass
let queryToThemesHash: Record<string, string> = {};
// populate with full theme terms
ActivitySubjects.Subjects.forEach(
  e => (queryToThemesHash[slugifyTitle(e)!] = e),
);
ActivitySubjects.Subjects.forEach(
  e => (queryToThemesHash[e.toLowerCase()] = e),
);

// add in other terms
const otherTerms = {
  health: "Health & Wellness",
  "study skills": "Life Skills",
  coding: "Coding & Tech",
  technology: "Coding & Tech",
  programming: "Coding & Tech",
  science: "Science & Nature",
  art: "Arts",
  ela: "English",
  "foreign language": "World Languages",
  "foreign languages": "World Languages",
};
Object.assign(queryToThemesHash, otherTerms);

const FILTER_CATCHALL_VALUES = {
  age: "All ages",
  theme: "All subjects",
  time: "Any time",
  timeOfDay: "Any time",
  format: "All formats",
  delivery: "Any length",
  startAfterTime: DAY_START_HOUR,
  endByTime: DAY_END_HOUR,
};

// Public search filter order values accepted from client URL/query params.
export const SEARCH_ACTIVITY_SORT_ORDERS = {
  new: "new",
  upcoming: "upcoming",
  relevance: "relevance",
  // No longer valid search filter sort orders:
  // popular_ranking: "popular_ranking",
  // saved: "saved",
  // popular: "popular",
  // recommended: "recommended",
} as const;
export type SearchActivitySortOrder =
  (typeof SEARCH_ACTIVITY_SORT_ORDERS)[keyof typeof SEARCH_ACTIVITY_SORT_ORDERS];

// looks for the param themeOrQuery and maps it into either the
// theme or q parameter as appropriate
export function mapThemeOrQuery(query: SearchFilterQuery): SearchFilterQuery {
  let queryCopy = Object.assign({}, query);

  if (queryCopy.themeOrQuery) {
    let theme = queryToThemesHash[queryCopy.themeOrQuery.toLowerCase()];
    if (theme) {
      queryCopy.theme = theme;
    } else {
      queryCopy.q =
        queryCopy.themeOrQuery + (queryCopy.q ? " " + queryCopy.q : "");
    }
    delete queryCopy.themeOrQuery;
  }

  if (queryCopy.theme && SEO.isTermWithinSubjectList(queryCopy.theme)) {
    queryCopy.theme = SEO.niceSubjectTerm(queryCopy.theme);
  }

  return queryCopy;
}

// used when a route populates themeOrQuery into a param
export function fromQueryAndParams(
  query: SearchFilterQuery,
  params: SearchFiltersRouteParams,
): SearchFilters {
  if (params.themeOrQuery) {
    const themeOrQuery = cleanSearchTerm(params.themeOrQuery);
    let queryCopy = Object.assign({}, query);
    queryCopy.themeOrQuery = themeOrQuery;

    return fromQuery(queryCopy);
  }
  return fromQuery(query);
}

export const SAVED_ORDER_OPTION_DEFAULT = "saved";

export const PRICE_PER_MEETING_FILTERS = [
  "pricePerMeetingMax",
  "pricePerMeetingMin",
];

export const PRICE_PER_COURSE_FILTERS = [
  "pricePerCourseMax",
  "pricePerCourseMin",
];

export const PRICE_CREDITS_PER_MEETING_FILTERS = [
  "priceCreditsPerMeetingMax",
  "priceCreditsPerMeetingMin",
];

export const PRICE_CREDITS_PER_COURSE_FILTERS = [
  "priceCreditsPerCourseMax",
  "priceCreditsPerCourseMin",
];

export const CAPACITY_FILTERS = ["capacityMax", "capacityMin"];

export const MEETING_DURATION_FILTERS = [
  "durationSessionMin",
  "durationSessionMax",
] as const;

// filters currently set from the query textbox
export const QUERY_FILTERS = ["q", "teacherName", "teacherUid"];

export const TIME_FILTERS = [
  "startAfterTime",
  "endByTime",
  "dow",
  "durationWeeksMax",
  "durationWeeksMin",
  "weeklyMeetingsMax",
  "weeklyMeetingsMin",
  ...MEETING_DURATION_FILTERS,
] as const;

export const CAPACITY_OPTIONS = [{}, { capacityMax: 1 }, { capacityMin: 2 }];

const FILTER_QUERY_PARAMETER_NAMES = [
  "adminTags",
  "age",
  "classTypes",
  "curriculums",
  "delivery",
  "dow",
  ...MEETING_DURATION_FILTERS,
  "durationWeeksMax",
  "durationWeeksMin",
  "enabledBooleanFilters",
  "endBy",
  "endByTime",
  "englishProficiencyLevel",
  "format",
  "fullCurriculum",
  "fundingPrograms",
  "gradeLevel",
  "hasAssessment",
  "hasHomework",
  "hasGrades",
  "includeInProgressFixedLengthLiveFormat",
  "includedWithMembership",
  "languageOfInstruction",
  "learningNeeds",
  "multiTermQuery",
  "order",
  "pricePerCourseMax",
  "pricePerCourseMin",
  "q",
  "standards",
  "startAfter",
  "startAfterTime",
  "startBefore",
  "theme",
  "themeOrQuery",
  "time",
  "timeOfDay",
  "topics",
  "userUid",
  "userName",
  "weeklyMeetingsMax",
  "weeklyMeetingsMin",
  "priceCreditsPerMeetingMax",
  "priceCreditsPerMeetingMin",
  "priceCreditsPerCourseMax",
  "priceCreditsPerCourseMin",
  ...PRICE_PER_MEETING_FILTERS,
  ...CAPACITY_FILTERS,
];

export function equal(
  filters1: Record<string, unknown>,
  filters2: Record<string, unknown>,
) {
  return (
    filters1 === filters2 ||
    FILTER_QUERY_PARAMETER_NAMES.every(param => {
      const filters1Param = comparableFilterValue(filters1[param]);
      const filters2Param = comparableFilterValue(filters2[param]);
      return filters1Param === filters2Param;
    })
  );
}

// Cached results from fromQuery to avoid re-parsing the same query string many times.
const filtersFromQueryCache = new Map<string, SearchFilters>();
export function fromQuery(query: SearchFilterQuery): SearchFilters {
  const queryString = JSON.stringify(query);
  if (filtersFromQueryCache.has(queryString)) {
    return filtersFromQueryCache.get(queryString)!;
  }

  const rawFilters = ldPick(
    query,
    FILTER_QUERY_PARAMETER_NAMES,
  ) as SearchFilterQuery;
  let filters: SearchFilterParserFilters = mapThemeOrQuery(rawFilters);

  const now = dayjs().startOf("day");
  const startAfter = stringFilterValue(filters, "startAfter");
  const endBy = stringFilterValue(filters, "endBy");
  const startBefore = stringFilterValue(filters, "startBefore");
  const startAfterDayjs = dateFilterToDayjs(startAfter);
  const endByDayjs = endBy !== catchallFor("endBy") && dateFilterToDayjs(endBy);
  const startBeforeDayjs = startBefore && dateFilterToDayjs(startBefore);
  splitCommaSeparatedFilter(filters, "multiTermQuery");
  splitCommaSeparatedFilter(filters, "adminTags");
  splitCommaSeparatedFilter(filters, "curriculums");
  splitCommaSeparatedFilter(filters, "fundingPrograms");
  splitCommaSeparatedFilter(filters, "standards");
  splitCommaSeparatedFilter(filters, "learningNeeds");
  splitCommaSeparatedFilter(filters, "topics");
  normalizeAgeFilter(filters);
  normalizeThemeFilter(filters);
  normalizeOrderFilter(filters);
  parseIntegerFilter(filters, "durationSessionMin");
  parseIntegerFilter(filters, "durationSessionMax");
  if (
    !!endByDayjs &&
    !isDateAfterOtherDates(endByDayjs, [now, startAfterDayjs])
  ) {
    delete filters.endBy;
  }

  // delete startBefore if it is before startAfter and not the same date
  if (
    !!startBeforeDayjs &&
    !startBeforeDayjs.isSame(startAfterDayjs) &&
    !isDateAfterOtherDates(startBeforeDayjs, [now, startAfterDayjs])
  ) {
    delete filters.startBefore;
  }

  // delete startAfter if its before today
  if (
    !!startAfterDayjs &&
    !isDateAfterOtherDates(startAfterDayjs, [now.subtract(1, "day")])
  ) {
    delete filters.startAfter;
  }

  if (!!filters.endBy && !!endByDayjs) {
    filters.endBy = endByDayjs.format(START_END_DATE_FORMAT);
  }
  if (!!filters.startBefore && !!startBeforeDayjs) {
    filters.startBefore = startBeforeDayjs.format(START_END_DATE_FORMAT);
  }
  if (!!filters.startAfter && !!startAfterDayjs) {
    filters.startAfter = startAfterDayjs.format(START_END_DATE_FORMAT);
  }
  const { startAfterTime, endByTime, includeInProgressFixedLengthLiveFormat } =
    filters;
  if (startAfterTime) {
    filters.startAfterTime = parseFloat(String(startAfterTime));
  }
  if (endByTime) {
    filters.endByTime = parseFloat(String(endByTime));
  }

  for (const booleanField of [
    "fullCurriculum",
    "hasAssessment",
    "hasGrades",
    "hasHomework",
    "includedWithMembership",
  ]) {
    if (Object.prototype.hasOwnProperty.call(filters, booleanField)) {
      filters[booleanField] = Boolean(
        String(filters[booleanField]).replace(
          /\s*(false|null|undefined|0)\s*/i,
          "",
        ),
      );
    }
  }
  for (const numberField of ["pricePerCourseMax", "pricePerCourseMin"]) {
    if (filters[numberField] && ldIsNumber(Number(filters[numberField]))) {
      filters[numberField] = Number(filters[numberField]);
    }
  }

  for (const integerField of [
    "capacityMax",
    "capacityMin",
    "durationWeeksMax",
    "durationWeeksMin",
    "pricePerMeetingMax",
    "pricePerMeetingMin",
    "priceCreditsPerMeetingMax",
    "priceCreditsPerMeetingMin",
    "priceCreditsPerCourseMax",
    "priceCreditsPerCourseMin",
    "weeklyMeetingsMax",
    "weeklyMeetingsMin",
  ]) {
    if (
      filters[integerField] &&
      ldIsNumber(parseInt(String(filters[integerField]), 10))
    ) {
      filters[integerField] = parseInt(String(filters[integerField]), 10);
    }
  }

  if (typeof filters.includeInProgressFixedLengthLiveFormat !== "undefined") {
    filters.includeInProgressFixedLengthLiveFormat = Boolean(
      String(includeInProgressFixedLengthLiveFormat || "").replace(
        /\s*(false|null|undefined|0)\s*/i,
        "",
      ),
    );
  }

  const result = setDefaultSearch(filters) as SearchFilters;
  filtersFromQueryCache.set(queryString, result);
  return result;
}

export function catchallFor(filterName: string) {
  return FILTER_CATCHALL_VALUES[
    filterName as keyof typeof FILTER_CATCHALL_VALUES
  ];
}

export function setDefaultSearch<T extends { q?: SearchFilterParserValue }>(
  filters: T,
) {
  if (
    !filters.q ||
    typeof filters.q !== "string" ||
    !filters.q.trim() ||
    filters.q === "one on one"
  ) {
    delete filters.q;
  }

  return filters;
}

// ie. Code & Tech --> coding-and-tech
export function convertThemeFilterToQuery(themes?: string | null) {
  return cleanThemeParam(themes);
}

// ie. coding-and-tech --> Code & Tech
export function convertThemeQueryToFilter(themes?: string | null) {
  return cleanThemeParam(themes, true);
}

export function cleanThemeParam(
  themes?: string | null,
  returnNiceTerms = false,
) {
  return themes
    ?.split(",")
    .filter(t => SEO.isTermWithinSubjectList(t))
    .map(t => (!returnNiceTerms ? SEO.cleanUrlTerm(t) : SEO.niceSubjectTerm(t)))
    .join(",");
}

export function omitCatchalls<T extends object>(filters: T): Partial<T> {
  return ldOmitBy(
    filters,
    (value, filterName) => value === catchallFor(filterName),
  ) as Partial<T>;
}

function cleanSearchTerm(searchTerm: string): string {
  return (
    searchTerm
      // replace dashes with spaces
      ?.replace(/c-plus-plus/i, "c++")
      .replace(/c-sharp/i, "c#")
      .replace(/-and-/i, "-&-")
      .replace(/-/g, " ")
      .trim()
      .toLowerCase()
  );
}

function stringFilterValue(
  filters: SearchFilterParserFilters,
  filterName: string,
): string | undefined {
  const value = filters[filterName];
  return typeof value === "string" ? value : undefined;
}

function splitCommaSeparatedFilter(
  filters: SearchFilterParserFilters,
  filterName: string,
) {
  const value = stringFilterValue(filters, filterName);
  if (value) {
    filters[filterName] = value.split(",");
  } else if (value === "") {
    delete filters[filterName];
  }
}

function normalizeAgeFilter(filters: SearchFilterParserFilters) {
  const age = stringFilterValue(filters, "age");
  if (!age) {
    return;
  }

  const normalizedAge = age
    .split(",")
    .map(age => parseInt(age, 10))
    .filter(age => age < 19)
    .join(",");

  if (normalizedAge) {
    filters.age = normalizedAge;
  } else {
    delete filters.age;
  }
}

function normalizeThemeFilter(filters: SearchFilterParserFilters) {
  const theme = stringFilterValue(filters, "theme");
  if (theme) {
    const normalizedTheme = convertThemeQueryToFilter(theme);
    if (normalizedTheme) {
      filters.theme = normalizedTheme;
    }
  }
}

function normalizeOrderFilter(filters: SearchFilterParserFilters) {
  const order = stringFilterValue(filters, "order");
  if (order && !isValidSortOrder(order)) {
    delete filters.order;
  }
}

function parseIntegerFilter(
  filters: SearchFilterParserFilters,
  filterName: string,
) {
  const value = filters[filterName];
  if (value) {
    filters[filterName] = parseInt(String(value), 10);
  }
}

function comparableFilterValue(value: unknown): unknown {
  return Array.isArray(value) ? value.join(",") : value;
}

function isValidSortOrder(order: string): order is SearchActivitySortOrder {
  return (
    Object.values(SEARCH_ACTIVITY_SORT_ORDERS) as readonly string[]
  ).includes(order);
}
